import { useCallback, useEffect, useState } from "react";
import { Bell, BellOff, CheckCheck, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

// Notifications inbox. GET /api/notifications returns
// { items: [{ id, title, body, read, dismissed?, created_at?, type?, cta? }],
//   unread, total, ... }.
// POST /notifications/read           { notification_id }
// POST /notifications/read-all
// POST /notifications/dismiss        { notification_id }
export default function Notifications() {
  const { api } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const { data } = await api.get("/notifications", { params: { limit: 50, offset: 0 } });
      setData(data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load notifications.");
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const markRead = async (n) => {
    if (n.read) return;
    setBusyId(n.id);
    try {
      await api.post("/notifications/read", { notification_id: n.id });
      load();
    } catch {
      // Non-blocking — surface via toast so it's clear something is off.
      toast.error("Could not mark as read.");
    } finally {
      setBusyId(null);
    }
  };

  const dismiss = async (n) => {
    setBusyId(n.id);
    try {
      await api.post("/notifications/dismiss", { notification_id: n.id });
      load();
    } catch {
      toast.error("Could not dismiss.");
    } finally {
      setBusyId(null);
    }
  };

  const markAllRead = async () => {
    try {
      const { data: r } = await api.post("/notifications/read-all");
      toast.success(`${r?.count || 0} marked as read.`);
      load();
    } catch { toast.error("Could not mark all as read."); }
  };

  if (error) return <div className="card-elev p-6" style={{ color: "var(--premium)" }}>{error}</div>;
  if (!data) return (
    <div className="max-w-3xl mx-auto py-12 flex justify-center">
      <Loader2 className="w-6 h-6 animate-spin" style={{ color: "var(--brand)" }} />
    </div>
  );

  const items = data.items || [];
  const unread = data.unread || 0;

  return (
    <div className="flex flex-col gap-5 md:gap-6" data-testid="notifications-page">
      <header className="flex items-start justify-between gap-3">
        <div>
          <div className="text-xs font-extrabold uppercase tracking-widest" style={{ color: "var(--on-surface-muted)" }}>
            Notifications
          </div>
          <h1 className="text-2xl md:text-3xl font-extrabold mt-1" style={{ color: "var(--on-surface)" }}>
            Inbox
          </h1>
          <p className="text-sm mt-1" style={{ color: "var(--on-surface-muted)" }}>
            {unread > 0 ? `${unread} unread` : "You're all caught up 🎉"}
          </p>
        </div>
        {unread > 0 && (
          <button onClick={markAllRead} className="btn btn-light shrink-0" data-testid="notif-mark-all">
            <CheckCheck className="w-4 h-4" /> Mark all read
          </button>
        )}
      </header>

      {items.length === 0 ? (
        <div className="card-elev p-8 text-center" data-testid="notif-empty">
          <BellOff className="w-8 h-8 mx-auto opacity-40" style={{ color: "var(--on-surface-muted)" }} />
          <div className="mt-2 text-lg font-extrabold" style={{ color: "var(--on-surface)" }}>No notifications yet</div>
          <div className="text-sm" style={{ color: "var(--on-surface-muted)" }}>
            We'll let you know about stamps, level ups, and campsite updates.
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-3" data-testid="notif-list">
          {items.map((n) => (
            <NotifRow key={n.id} n={n} busy={busyId === n.id}
                      onRead={() => markRead(n)} onDismiss={() => dismiss(n)} />
          ))}
        </div>
      )}
    </div>
  );
}

function NotifRow({ n, busy, onRead, onDismiss }) {
  return (
    <article
      onClick={onRead}
      className="card-elev p-4 flex items-start gap-3 cursor-pointer transition"
      style={{ opacity: busy ? 0.6 : 1 }}
      data-testid={`notif-${n.id}`}
    >
      <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0 relative"
           style={{ background: n.read ? "color-mix(in srgb, var(--on-surface-muted) 10%, transparent)"
                                       : "color-mix(in srgb, var(--brand) 15%, transparent)",
                    color: n.read ? "var(--on-surface-muted)" : "var(--brand)" }}>
        <Bell className="w-5 h-5" />
        {!n.read && (
          <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full"
                style={{ background: "var(--premium)" }} />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <div className="text-sm font-extrabold" style={{ color: n.read ? "var(--on-surface-muted)" : "var(--on-surface)" }}>
            {n.title || "Update"}
          </div>
          <div className="text-[10px] shrink-0" style={{ color: "var(--on-surface-muted)" }}>
            {(n.created_at || "").slice(0, 10) || ""}
          </div>
        </div>
        {n.body && (
          <p className="text-sm mt-1 leading-relaxed" style={{ color: n.read ? "var(--on-surface-muted)" : "var(--on-surface)" }}>
            {n.body}
          </p>
        )}
      </div>
      <button
        onClick={(e) => { e.stopPropagation(); onDismiss(); }}
        className="p-2 rounded-lg hover:bg-black/5 shrink-0"
        title="Dismiss" aria-label="Dismiss"
        data-testid={`notif-dismiss-${n.id}`}
      >
        <Trash2 className="w-3.5 h-3.5 opacity-60" />
      </button>
    </article>
  );
}
